import { useLayoutEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';

import type { Workspace } from '../../components/screens/RunScreens';
import { formatFileSize } from '../../components/workflowShared';
import AppMenu from './AppMenu';
import {
  isContextLink,
  VERIFIABLE_SECTIONS,
  type AppContextEntry,
  type AppContextSection,
  type AppContextStatus
} from './appContextStore';
import {
  IconAlert,
  IconCheckCircle,
  IconClose,
  IconFile,
  IconFileText,
  IconLink,
  IconMinus,
  IconPaperclip,
  IconPlus
} from './appIcons';

interface AppContextViewProps {
  workspace: Workspace;
  entries: AppContextEntry[];
  saveError?: string;
  onAdd: (section: AppContextSection, text: string) => void;
  onUpdate: (entryId: string, change: Partial<Pick<AppContextEntry, 'text' | 'status'>>) => void;
  onRemove: (entryId: string) => void;
  /** Resolves to an error message, or an empty string when every file was added. */
  onAddFiles: (files: File[]) => Promise<string>;
  onOpenFile: (entry: AppContextEntry) => Promise<void>;
}

interface SectionInfo {
  id: AppContextSection;
  title: string;
  description: string;
  noun: string;
  placeholder: string;
  /** Topics offered while the section is empty; each starts an entry with the topic as its prefix. */
  starters: string[];
}

interface ContextStat {
  label: string;
  value: string;
  tone?: 'danger';
}

interface StatusMenu {
  anchor: HTMLElement;
  entryId: string;
}

// Worded for any kind of application: a SaaS product, an internal tool, an API or a protocol.
const SECTIONS: SectionInfo[] = [
  {
    id: 'overview',
    title: 'Overview',
    description: 'What it does, who relies on it, and what must keep working.',
    noun: 'detail',
    placeholder: 'e.g. Customer portal for orders and invoices, used by our B2B customers',
    starters: ['Purpose', 'Users', 'Critical operations', 'Sensitive data']
  },
  {
    id: 'boundaries',
    title: 'Trust boundaries',
    description: 'The systems, providers and people it depends on, and where data or privileges cross between them.',
    noun: 'boundary',
    placeholder: 'e.g. Partners call the orders API with a per-tenant API key',
    starters: ['Public entry points', 'Admin access', 'Integrations', 'Third parties']
  },
  {
    id: 'preconditions',
    title: 'Preconditions',
    description: 'What must already be true for it to be secure: deployment, configuration, environment or roles.',
    noun: 'precondition',
    placeholder: 'e.g. The admin panel is only reachable over the company VPN',
    starters: ['Deployment', 'Configuration', 'Network access', 'Roles']
  },
  {
    id: 'assumptions',
    title: 'Trust assumptions',
    description: 'Who and what is trusted, and how far. Mark an assumption verified once evidence backs it.',
    noun: 'assumption',
    placeholder: 'e.g. The identity provider enforces MFA for every administrator',
    starters: ['Administrators', 'Identity provider', 'Suppliers', 'Users']
  },
  {
    id: 'documentation',
    title: 'Documentation',
    description: 'Architecture notes, specs, policies and links that explain how it is meant to work.',
    noun: 'link or note',
    placeholder: 'Paste a link or write a note',
    starters: []
  }
];

const STATUS_LABELS: Record<AppContextStatus, string> = {
  unverified: 'Unverified',
  verified: 'Verified',
  disproven: 'Disproven'
};

const STATUS_ICONS: Record<AppContextStatus, ReactNode> = {
  unverified: <IconMinus aria-hidden={true} />,
  verified: <IconCheckCircle aria-hidden={true} />,
  disproven: <IconAlert aria-hidden={true} />
};

/** A one-line textarea that grows with its text. Enter saves, Shift+Enter adds a line, Escape closes. */
function ContextEditor({
  initialValue = '',
  placeholder,
  keepOpen = false,
  onSubmit,
  onClose
}: {
  initialValue?: string;
  placeholder: string;
  /** Clears and stays open after saving, so several entries can be added in a row. */
  keepOpen?: boolean;
  onSubmit: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Unmounting after Enter/Escape can still fire blur; only the first outcome counts.
  const isSettledRef = useRef(false);
  // An untouched starter prefix such as "Purpose:" is not an entry.
  const hasContent = Boolean(value.trim()) && value.trim() !== initialValue.trim();

  useLayoutEffect(() => {
    const textarea = textareaRef.current;

    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${String(textarea.scrollHeight)}px`;
    }
  }, [value]);

  const close = (submit: boolean) => {
    if (isSettledRef.current) {
      return;
    }

    isSettledRef.current = true;

    if (submit && hasContent) {
      onSubmit(value.trim());
    }

    onClose();
  };

  return (
    <div className="app-context-editor">
      <textarea
        ref={textareaRef}
        rows={1}
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={true}
        onFocus={(event) => {
          const end = event.currentTarget.value.length;

          event.currentTarget.setSelectionRange(end, end);
        }}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => close(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            close(false);
          } else if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();

            if (keepOpen && hasContent) {
              onSubmit(value.trim());
              setValue('');
            } else {
              close(true);
            }
          }
        }}
      />
      <span className="app-context-hint" aria-hidden={true}>Enter to save · Shift+Enter for a new line · Esc to close</span>
    </div>
  );
}

function ContextItem({
  entry,
  noun,
  isVerifiable,
  isStatusMenuOpen,
  onEdit,
  onRemove,
  onOpenFile,
  onOpenStatus
}: {
  entry: AppContextEntry;
  noun: string;
  isVerifiable: boolean;
  isStatusMenuOpen: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onOpenFile: () => void;
  onOpenStatus: (anchor: HTMLElement) => void;
}) {
  const label = entry.file ? entry.text : entry.text.split('\n')[0];
  const status = entry.status ?? 'unverified';

  return (
    <li className={`app-context-item${isStatusMenuOpen ? ' menu-open' : ''}`}>
      {entry.file ? (
        <button type="button" className="app-context-item-main" title="Open document" onClick={onOpenFile}>
          <span className="app-context-marker"><IconFile aria-hidden={true} /></span>
          <span className="app-context-item-text">{entry.text}</span>
          <span className="app-context-item-meta">{formatFileSize(entry.file.size)}</span>
        </button>
      ) : isContextLink(entry.text) ? (
        <a className="app-context-item-main" href={entry.text.trim()} target="_blank" rel="noopener noreferrer">
          <span className="app-context-marker"><IconLink aria-hidden={true} /></span>
          <span className="app-context-item-text">{entry.text.trim()}</span>
        </a>
      ) : (
        <button type="button" className="app-context-item-main" aria-label={`Edit ${noun}: ${label}`} onClick={onEdit}>
          <span className="app-context-marker">
            {entry.section === 'documentation'
              ? <IconFileText aria-hidden={true} />
              : <span className="app-context-dot" />}
          </span>
          <span className="app-context-item-text">{entry.text}</span>
        </button>
      )}
      {isVerifiable && (
        <button
          type="button"
          className={`app-context-status ${status}`}
          aria-haspopup="menu"
          aria-expanded={isStatusMenuOpen}
          aria-label={`${STATUS_LABELS[status]}. Change status of ${label}`}
          onClick={(event) => onOpenStatus(event.currentTarget)}
        >
          {STATUS_LABELS[status]}
        </button>
      )}
      <button
        type="button"
        className="app-context-remove"
        aria-label={`Remove ${label}`}
        title="Remove"
        onClick={onRemove}
      >
        <IconClose aria-hidden={true} />
      </button>
    </li>
  );
}

/**
 * What Havu should know about an application before it assesses it: one quiet document of short
 * lists that save as you type, with a single inline editor open at a time.
 */
function AppContextView({
  workspace,
  entries,
  saveError,
  onAdd,
  onUpdate,
  onRemove,
  onAddFiles,
  onOpenFile
}: AppContextViewProps) {
  const [adding, setAdding] = useState<{ section: AppContextSection; prefix: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [statusMenu, setStatusMenu] = useState<StatusMenu | null>(null);
  const [isDropping, setIsDropping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileError, setFileError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const countSection = (section: AppContextSection) => entries.filter((entry) => entry.section === section).length;
  const claims = entries.filter((entry) => VERIFIABLE_SECTIONS.includes(entry.section));
  const verifiedCount = claims.filter((entry) => entry.status === 'verified').length;
  const disprovenCount = claims.filter((entry) => entry.status === 'disproven').length;
  const disprovenStats: ContextStat[] = disprovenCount > 0
    ? [{ label: 'Disproven', value: String(disprovenCount), tone: 'danger' }]
    : [];
  const stats: ContextStat[] = [
    { label: 'Described', value: String(countSection('overview')) },
    { label: 'Boundaries', value: String(countSection('boundaries')) },
    { label: 'Verified', value: `${String(verifiedCount)} of ${String(claims.length)}` },
    ...disprovenStats,
    { label: 'Documents', value: String(countSection('documentation')) }
  ];

  const startAdding = (section: AppContextSection, prefix = '') => {
    setEditingId(null);
    setAdding({ section, prefix });
  };

  const addFiles = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    setIsUploading(true);
    setFileError('');
    onAddFiles(files)
      .then(setFileError)
      .finally(() => setIsUploading(false));
  };

  const openFile = (entry: AppContextEntry) => {
    setFileError('');
    onOpenFile(entry).catch((error: unknown) => {
      setFileError(error instanceof Error && error.message ? error.message : 'The document could not be opened.');
    });
  };

  const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files');
  const menuEntry = statusMenu ? entries.find((entry) => entry.id === statusMenu.entryId) : undefined;

  return (
    <div className="app-scroll">
      <div className="app-context">
        <header className="app-context-intro">
          <p className="app-context-lead">
            {`What Havu should know about ${workspace.name} before it assesses it. A sentence per entry is enough.`}
          </p>
          {entries.length > 0 && (
            <dl className="app-context-stats">
              {stats.map((stat) => (
                <div key={stat.label} className={stat.tone}>
                  <dt>{stat.label}</dt>
                  <dd>{stat.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </header>

        {SECTIONS.map((section) => {
          const sectionEntries = entries.filter((entry) => entry.section === section.id);
          const isDocumentation = section.id === 'documentation';
          const headingId = `app-context-${section.id}`;

          return (
            <section
              key={section.id}
              className={`app-context-section${isDocumentation && isDropping ? ' dropping' : ''}`}
              aria-labelledby={headingId}
              onDragOver={isDocumentation ? (event) => {
                if (hasFiles(event)) {
                  event.preventDefault();
                  setIsDropping(true);
                }
              } : undefined}
              onDragLeave={isDocumentation ? (event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                  setIsDropping(false);
                }
              } : undefined}
              onDrop={isDocumentation ? (event) => {
                event.preventDefault();
                setIsDropping(false);
                addFiles(Array.from(event.dataTransfer.files));
              } : undefined}
            >
              <header className="app-context-head">
                <h2 id={headingId}>{section.title}</h2>
                <p>{section.description}</p>
              </header>

              {sectionEntries.length > 0 && (
                <ul className="app-context-list">
                  {sectionEntries.map((entry) => editingId === entry.id ? (
                    <li key={entry.id}>
                      <ContextEditor
                        initialValue={entry.text}
                        placeholder={section.placeholder}
                        onSubmit={(text) => onUpdate(entry.id, { text })}
                        onClose={() => setEditingId(null)}
                      />
                    </li>
                  ) : (
                    <ContextItem
                      key={entry.id}
                      entry={entry}
                      noun={section.noun}
                      isVerifiable={VERIFIABLE_SECTIONS.includes(section.id)}
                      isStatusMenuOpen={statusMenu?.entryId === entry.id}
                      onEdit={() => {
                        setAdding(null);
                        setEditingId(entry.id);
                      }}
                      onRemove={() => onRemove(entry.id)}
                      onOpenFile={() => openFile(entry)}
                      onOpenStatus={(anchor) => {
                        setStatusMenu((current) => current?.anchor === anchor ? null : { anchor, entryId: entry.id });
                      }}
                    />
                  ))}
                </ul>
              )}

              {adding?.section === section.id ? (
                <ContextEditor
                  key={adding.prefix}
                  initialValue={adding.prefix}
                  placeholder={section.placeholder}
                  keepOpen={true}
                  onSubmit={(text) => onAdd(section.id, text)}
                  onClose={() => setAdding(null)}
                />
              ) : (
                <div className="app-context-actions">
                  <button type="button" className="app-context-add" onClick={() => startAdding(section.id)}>
                    <IconPlus aria-hidden={true} />
                    <span>{`Add ${section.noun}`}</span>
                  </button>
                  {isDocumentation && (
                    <button
                      type="button"
                      className="app-context-add"
                      disabled={isUploading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {isUploading ? <span className="app-spinner" aria-hidden={true} /> : <IconPaperclip aria-hidden={true} />}
                      <span>Upload files</span>
                    </button>
                  )}
                  {sectionEntries.length === 0 && section.starters.length > 0 && (
                    <span className="app-context-starters" aria-label={`Start a ${section.noun} about`}>
                      {section.starters.map((starter) => (
                        <button
                          key={starter}
                          type="button"
                          className="app-context-starter"
                          onClick={() => startAdding(section.id, `${starter}: `)}
                        >
                          {starter}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              )}

              {isDocumentation && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple={true}
                    hidden={true}
                    onChange={(event) => {
                      addFiles(Array.from(event.target.files ?? []));
                      event.target.value = '';
                    }}
                  />
                  {isDropping && <p className="app-context-drop" aria-hidden={true}>Drop files to add them</p>}
                  {fileError && <p className="app-widget-error" role="alert">{fileError}</p>}
                </>
              )}
            </section>
          );
        })}

        {saveError
          ? <p className="app-widget-error" role="alert">{saveError}</p>
          : <p className="app-context-note">Changes save automatically to this workspace.</p>}
      </div>

      {statusMenu && menuEntry && (
        <AppMenu
          anchor={statusMenu.anchor}
          items={(Object.keys(STATUS_LABELS) as AppContextStatus[]).map((status) => ({
            label: STATUS_LABELS[status],
            icon: STATUS_ICONS[status],
            meta: (menuEntry.status ?? 'unverified') === status ? 'Current' : undefined,
            onSelect: () => onUpdate(menuEntry.id, { status })
          }))}
          onClose={() => setStatusMenu(null)}
        />
      )}
    </div>
  );
}

export default AppContextView;
